// @ts-nocheck
import { Dictionary } from "@insite/client-framework/Common/Types";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import FieldDefinition, { ChildFieldDefinition } from "@insite/client-framework/Types/FieldDefinition";
import { TabDefinition } from "@insite/client-framework/Types/TabDefinition";
import { FieldDefinitionsBuilderOptions } from "@store/Framework/Features/FieldDefinitionBuilder/FieldDefinitionBuilderTypes";
import imageFieldDictionary from "@store/Framework/Features/FieldDefinitionBuilder/Fields/imageFieldDictionary";
import typographyFieldDictionary from "@store/Framework/Features/FieldDefinitionBuilder/Fields/typographyFieldDictionary";
import buildFields from "@store/Framework/Features/FieldDefinitionBuilder/Utilities/buildFields";
import { buildResponsiveFields } from "@store/Framework/Features/FieldDefinitionBuilder/Utilities/buildResponsiveFields";
import { PartialBy } from "@store/Framework/Types/Common";
import { FoundationFieldDefinition } from "@store/Framework/Types/FoundationFieldDefinition";
import { breakpointsValues } from "@store/Themes/common/styleguide/config/breakpoints";
import cloneDeep from "lodash/cloneDeep";

type ResponsiveOptionsValuesMapperOptions<TValue = string> = {
    overridedDefaultValuesObj?: HasFields;
    valueToMap?: TValue;
};

export const mapValues = (values: Dictionary<any>, options?: ResponsiveOptionsValuesMapperOptions) => {
    const valuesFields: HasFields = options?.overridedDefaultValuesObj || {
        fields: {},
    };

    for (const key in values) {
        if (Object.prototype.hasOwnProperty.call(values, key)) {
            const element = values[key];
            valuesFields.fields[key] = element;
        }
    }

    return valuesFields;
};

export const getBreakpointFromTitle = (breakpointTitle: string) => Number(breakpointTitle.match(/\d+/g));

const setBreakpointTitle = (breakpoint: number) => `${breakpoint}px`;

export const mapValuesPerBreakpoint = (values: Dictionary<any>, breakpoint: number) => {
    const defaultValuesFields: HasFields = {
        fields: {},
    };

    defaultValuesFields.fields.breakpoint = setBreakpointTitle(breakpoint);

    mapValues(values, {
        overridedDefaultValuesObj: defaultValuesFields,
    });

    return defaultValuesFields;
};

export const getValuesPerBreakpoint = (values: Dictionary<any>) => {
    const breakpoints = [...breakpointsValues];
    breakpoints.shift();

    if (breakpoints?.length) {
        return breakpoints?.map((x: number) => {
            return mapValuesPerBreakpoint(values, x);
        });
    }

    return [];
};

export const fieldDefinitionsToDefaultValuesDictionary = (
    fieldDefinitions: FieldDefinition[] | ChildFieldDefinition[],
) => {
    const values: Dictionary<any> = {};

    for (let index = 0; index < fieldDefinitions.length; index++) {
        const element = fieldDefinitions[index];
        values[element.name] = element.defaultValue;
    }

    return values;
};

/**
 *
 * @param options
 * @returns
 */
const resolveOptionsFields = (options?: FieldDefinitionsBuilderOptions) => {
    const basicFields: FoundationFieldDefinition[] = [];
    const responsiveFields: FoundationFieldDefinition[] = [];

    // TODO - automate this part in another kaizen
    const typographyFieldDefinitions = buildFields(typographyFieldDictionary, options?.typographyOptions);
    const imageFieldDefinitions = buildFields(imageFieldDictionary, options?.imageOptions);
    const optionsFields = [...typographyFieldDefinitions, ...imageFieldDefinitions];

    optionsFields.forEach(x => {
        for (const key in options) {
            const option = (options as any)[key];

            if (option[x.name]) {
                if (option[x.name]?.hasResponsive) {
                    responsiveFields.push(x);
                    break;
                } else {
                    basicFields.push(x);
                    break;
                }
            }
        }
    });

    return {
        basicFields,
        responsiveFields,
    };
};

/**
 * Converts a set of fieldDefinitions into a set of childFieldDefinitions
 *
 * @param fieldDefinitions FieldDefinitions to convert
 * @returns A set of childFieldDefinitions
 */
const toChildFieldDefinition = (fieldDefinitions: FoundationFieldDefinition[]) => {
    return (
        (fieldDefinitions as PartialBy<FieldDefinition, "fieldType">[])?.map(x => {
            if (x.fieldType) {
                delete x.fieldType;
            }

            return x as ChildFieldDefinition;
        }) || []
    );
};

/**
 *
 * Applies a tab to a set of fieldDefinitions
 *
 * @param tab The fieldDefinitions tab
 * @param fieldDefinitions The fieldDefinitions to apply the tab to.
 * @returns A clone of the given fieldDefinitions with the tab applied to.
 */
const withTab = (tab: TabDefinition, fieldDefinitions: FoundationFieldDefinition[]) => {
    if (!fieldDefinitions.length) {
        return [];
    }

    return cloneDeep(fieldDefinitions).map(x => {
        if (!x.tab) {
            x.tab = tab;
        }

        return x;
    });
};

export default (
    fieldDefinitions: FoundationFieldDefinition[],
    options?: FieldDefinitionsBuilderOptions,
): FoundationFieldDefinition[] => {
    const basicTab = {
        displayName: "Basic",
        sortOrder: 0,
    };

    const responsiveTab = {
        displayName: "Responsive",
        sortOrder: 1,
    };

    const baseFieldDefinitions = [...(fieldDefinitions || [])];

    const buildFieldDefinitions = (): FoundationFieldDefinition[] => {
        const { basicFields, responsiveFields } = resolveOptionsFields(options);

        const fields = [
            ...withTab(basicTab, [...basicFields]),
            ...(responsiveFields.length || baseFieldDefinitions.length
                ? withTab(responsiveTab, [
                      ...baseFieldDefinitions,
                      ...responsiveFields,
                      ...buildResponsiveFields([
                          ...toChildFieldDefinition(baseFieldDefinitions),
                          ...toChildFieldDefinition(responsiveFields),
                      ]),
                  ])
                : []),
        ];

        return fields;
    };

    return buildFieldDefinitions();
};
